import {
  Box,
  Flex,
  Text,
  IconButton,
  Button,
  Stack,
  Collapse,
  Icon,
  useColorModeValue,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  GridItem,
  Grid,
  Switch,
  Image,
  HStack,
  Tooltip,
} from "@chakra-ui/react";
import {
  HamburgerIcon,
  CloseIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowBackIcon,
} from "@chakra-ui/icons";

import {
  FiList,
  FiFileText,
  FiEye,
  FiCalendar,
  FiFolder,
  FiBook,
  FiCamera,
  FiArchive,
  FiActivity,
  FiFile,
  FiUpload,
  FiDatabase,
  FiUser,
  FiMoreHorizontal,
  FiLogOut,
} from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import IframeComponent from "../CommonComponents/IframeComponent";
import Breadcrumbs from "../FunctionalComponents/Breadcrumb/Breadcrumb";
import { togglePreview } from "../redux/actions/previewActions";
import { useEffect, useState } from "react";
import { adminProfile } from "../constants/constants";
import { GlobalSearch } from "../CommonComponents/GlobalSearch";
import { useLogout } from "../hooks/useLogout";
const Logo = `https://d3f7r9v35rq5qh.cloudfront.net/ui/Logo/TRIS-logo-Image.svg`;
const TextLogo = `https://d3f7r9v35rq5qh.cloudfront.net/ui/Logo/TRIS-logo-text.svg`;

// Icon mapping for navigation items
const getNavIcon = (label) => {
  const iconMap = {
    Masterlists: FiList,
    Posts: FiFileText,
    Insights: FiEye,
    Calendar: FiCalendar,
    Projects: FiFolder,
    "Flip Books": FiBook,
    "Virtual Tours": FiCamera,
    Events: FiCalendar,
    Objects: FiArchive,
    Agraphy: FiDatabase,
    More: FiMoreHorizontal,
    Public: FiFileText,
    "Audit log": FiActivity,
    // Child items
    Film: FiCamera,
    Personality: FiUser,
    Library: FiBook,
    Archive: FiArchive,
    Videos: FiCamera,
    "Document Types": FiFileText,
    "File List": FiFile,
    "Derive File": FiFile,
    "Load Data": FiUpload,
    "Load File": FiUpload,
    "Focus of the day": FiCalendar,
    "Flip book": FiBook,
    "Virtual tour": FiCamera,
  };

  return iconMap[label] || FiFile; // Default icon
};

// Function to check if navigation item is active
const isNavItemActive = (navItem, currentPath) => {
  // Direct href match
  if (navItem.href && currentPath === navItem.href) {
    return true;
  }

  // Check if any child is active
  if (navItem.children) {
    return navItem.children.some((child) => currentPath === child.href);
  }

  return false;
};

// Function to check if child item is active
const isChildItemActive = (childHref, currentPath) => {
  return currentPath === childHref;
};

// Function to capitalize first letter
const capitalizeFirstLetter = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export default function RootLayout() {
  const { isOpen, onToggle } = useDisclosure();
  const { isOpen: isSidebarCollapsed, onToggle: toggleSidebar } =
    useDisclosure();
  const isLoggedIn = useSelector((state) => state.session.isAuthenticated);
  const user = useSelector((state) => state.session.user);
  const username = useSelector((state) => state.session.username);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const previewEnabledFromState = useSelector(
    (state) => state.preview.isEnabled,
  );

  // Get display name with first letter capitalized
  const displayName = capitalizeFirstLetter(
    username ||
      user?.username ||
      user?.name ||
      user?.user?.username ||
      user?.user?.name ||
      "User",
  );
  const mainBgColor = "linear-gradient(270deg, #000000 -120%, #ffffff 62%)";
  const navBgColor = useColorModeValue("white", "white");
  const navBorderColor = useColorModeValue("gray.200", "gray.900");

  // Check if we're in production environment
  const isProduction =
    process.env.REACT_APP_MONGODB_URL?.includes("https://api.");

  // Define base URLs for logo links
  const baseUrl = isProduction
    ? "https://tuliresearchcentre.org"
    : "https://staging.tuliresearchcentre.org";
  const logoHomeUrl = baseUrl;
  const logoExploreUrl = `${baseUrl}/explore`;

  // Disable preview in production environment
  const previewEnabled = isProduction ? false : previewEnabledFromState;

  // Use reddish background for main container when in production
  const productionMainBgColor = useColorModeValue(
    "linear-gradient(227deg, #1a365d -70%, #ffffff 75%)",
  );
  const finalMainBgColor = isProduction ? productionMainBgColor : mainBgColor;

  const handleLogout = useLogout();

  const handlePreviewToggle = () => {
    dispatch(togglePreview());
  };

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/admin-login");
    }
  }, [isLoggedIn, navigate]);

  // Mirror a logout that happened in another tab: localStorage.clear()
  // (used by every logout path) fires a 'storage' event with key === null
  // in every OTHER same-origin tab. Without this, another open tab keeps
  // showing as authenticated until its own next API call happens to fail.
  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === null || event.key === "isAuthenticated") {
        if (event.newValue !== "true") {
          handleLogout();
        }
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [handleLogout]);

  return isLoggedIn ? (
    <>
      <Box
        zIndex={999}
        position={"fixed"}
        width={"100%"}
        top={0}
        bg={finalMainBgColor}
      >
        {/* Header */}
        <Flex
          color={navBgColor}
          py={{ base: 2 }}
          mx={{ base: 4 }}
          borderBottom={1}
          borderStyle={"solid"}
          borderColor={navBorderColor}
          align={"center"}
          gap={"10px"}
          justifyContent={"space-between"}
        >
          <Flex alignItems="center" gap={3}>
            {/* Desktop sidebar toggle - positioned at the far left */}
            <IconButton
              onClick={toggleSidebar}
              icon={<HamburgerIcon w={4} h={4} />}
              variant={"ghost"}
              aria-label={"Toggle Sidebar"}
              display={{ base: "none", md: "flex" }}
              size="md"
              borderRadius="md"
              _hover={{
                bg: "gray.100",
                transform: "scale(1.05)",
              }}
              _active={{
                transform: "scale(0.95)",
              }}
              transition="all 0.2s ease"
            />

            <Stack flexDirection={"row"}>
              <HStack display={{ base: "none", md: "flex" }}>
                <Link
                  to={logoHomeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Image
                    pl={{ base: 1, md: 0 }}
                    src={Logo}
                    width="auto"
                    height={"58px"}
                  />
                </Link>
              </HStack>
              <HStack className="mt-0" alignItems={"flex-end"}>
                <Link
                  to={logoExploreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Box
                    mr={{ base: 0, md: "10px" }}
                    marginRight={{ base: "5px", md: "0px" }}
                    borderRadius={"10px"}
                    border={"1px"}
                    borderColor={"#e8e8e8"}
                  >
                    <Image
                      px="2"
                      py="1"
                      src={TextLogo}
                      width="auto"
                      height={"50px"}
                    />
                  </Box>
                </Link>
              </HStack>
            </Stack>
          </Flex>

          <Flex
            flex={{ base: 1, md: "auto" }}
            ml={{ base: -2 }}
            display={{ base: "flex", md: "none" }}
            alignItems="center"
          >
            {/* Mobile menu toggle */}
            <IconButton
              onClick={onToggle}
              icon={
                isOpen ? (
                  <CloseIcon w={3} h={3} />
                ) : (
                  <HamburgerIcon w={5} h={5} />
                )
              }
              variant={"ghost"}
              aria-label={"Toggle Mobile Navigation"}
            />
          </Flex>
          <Flex
            flex={{ base: 1 }}
            justify={{ base: "center", md: "end" }}
            alignItems="center"
            gap={4}
          >
            {/* Global Search Component */}
            <GlobalSearch />
            {!isProduction && (
              <Box
                display="flex"
                alignItems="center"
                bg="gray.50"
                borderRadius="full"
                px={4}
                py={2}
                border="1px"
                borderColor="gray.200"
                shadow="sm"
              >
                <Text fontSize="14px" fontWeight="500" color="gray.700" mr={3}>
                  {previewEnabled ? "Preview On" : "Preview Off"}
                </Text>
                <Switch
                  id="preview-switch"
                  isChecked={previewEnabled}
                  onChange={handlePreviewToggle}
                  colorScheme="blue"
                  size="md"
                />
              </Box>
            )}

            <Menu>
              <MenuButton
                as={Button}
                variant="ghost"
                p={1}
                borderRadius="full"
                _hover={{ bg: "gray.100" }}
                _active={{ bg: "gray.200" }}
              >
                <Flex alignItems="center" gap={2}>
                  <Avatar
                    size="sm"
                    bg="blue.500"
                    color="white"
                    name={displayName}
                  />
                  <Icon as={ChevronDownIcon} boxSize={4} color="gray.600" />
                </Flex>
              </MenuButton>
              <MenuList
                shadow="lg"
                border="1px"
                borderColor="gray.200"
                borderRadius="md"
                py={2}
              >
                <Box px={3} py={2} borderBottom="1px" borderColor="gray.100">
                  <Text fontSize="sm" fontWeight="600" color="gray.900">
                    {displayName}
                  </Text>
                  {(user?.role || user?.user?.role) && (
                    <Text fontSize="xs" color="gray.500">
                      {capitalizeFirstLetter(user?.role || user?.user?.role)}
                    </Text>
                  )}
                </Box>
                <MenuItem
                  onClick={handleLogout}
                  color="red.600"
                  _hover={{ bg: "red.50" }}
                  py={3}
                  px={3}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Icon as={FiLogOut} boxSize={4} />
                    <Text>{adminProfile.LOGOUT}</Text>
                  </Box>
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
        </Flex>
        {/* Mobile Navigation */}
        <Collapse in={isOpen} animateOpacity>
          <MobileNav />
        </Collapse>
      </Box>
      {/* Main layout with sidebar and content */}
      <Flex height="calc(100vh - 79px)" width="100%" position="relative">
        {/* Left Sidebar - Desktop only */}
        <Box
          display={{ base: "none", md: "block" }}
          width={isSidebarCollapsed ? "60px" : "250px"}
          height="calc(100vh - 68px)"
          bg={
            isProduction
              ? "linear-gradient(180deg, #1a365d 0%, #2c5282 100%)"
              : "gray.900"
          }
          borderRight="1px"
          borderColor={isProduction ? "blue.600" : "gray.700"}
          overflowY={isSidebarCollapsed ? "visible" : "auto"}
          overflowX="visible"
          position="fixed"
          top="68px"
          left="0"
          zIndex="100"
          transition="width 0.3s ease"
          css={{
            "&::-webkit-scrollbar": {
              width: "3px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: isProduction ? "#3182ce" : "#4a5568",
              borderRadius: "2px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: isProduction ? "#2c5aa0" : "#2d3748",
            },
            // Firefox scrollbar styling
            scrollbarWidth: "thin",
            scrollbarColor: isProduction
              ? "#3182ce transparent"
              : "#4a5568 transparent",
          }}
        >
          <Flex direction="column" height="100%">
            <Box flex="1" overflowY="auto" pb={4}>
              <SidebarNav isCollapsed={isSidebarCollapsed} />
            </Box>

            {/* Sticky Back Button at Bottom */}
            <Box
              p={isSidebarCollapsed ? 2 : 4}
              borderTop="1px"
              borderColor={isProduction ? "blue.500" : "gray.600"}
              bg={
                isProduction
                  ? "linear-gradient(180deg, #2c5282 0%, #1a365d 100%)"
                  : "#171923"
              }
              color={"white"}
              position="sticky"
              bottom={0}
              zIndex="10"
            >
              <Tooltip
                label={isSidebarCollapsed ? "Go Back" : ""}
                placement="right"
                isDisabled={!isSidebarCollapsed}
              >
                <Button
                  onClick={() => navigate(-1)}
                  leftIcon={!isSidebarCollapsed ? <ArrowBackIcon /> : undefined}
                  variant="ghost"
                  color="white"
                  size={isSidebarCollapsed ? "sm" : "md"}
                  width="100%"
                  _hover={{
                    bg: "gray.800",
                    color: "white",
                  }}
                  justifyContent={isSidebarCollapsed ? "center" : "flex-start"}
                >
                  {isSidebarCollapsed ? <ArrowBackIcon /> : "Go Back"}
                </Button>
              </Tooltip>
            </Box>
          </Flex>
        </Box>

        {/* Main Content Area */}
        <Flex
          flex="1"
          direction="column"
          ml={{
            base: "0",
            md: isSidebarCollapsed ? "60px" : "250px",
          }}
          transition="margin-left 0.3s ease"
          marginTop={"75px"}
        >
          <Breadcrumbs />
          <Grid
            templateColumns={previewEnabled ? "60% 40%" : "100%"}
            height="calc(100vh - 120px)"
            width="100%"
          >
            {previewEnabled && (
              <GridItem>
                <IframeComponent />
              </GridItem>
            )}
            <GridItem>
              <Outlet />
            </GridItem>
          </Grid>
        </Flex>
      </Flex>
    </>
  ) : (
    <Outlet />
  );
}
const SidebarNav = ({ isCollapsed = false }) => {
  const location = useLocation();

  // Always call hooks unconditionally
  const defaultLinkHoverColor = useColorModeValue("gray.900", "white");
  const defaultHoverBgColor = useColorModeValue("gray.50", "gray.600");

  // Check if we're in production environment
  const isProduction =
    process.env.REACT_APP_MONGODB_URL?.includes("https://api.");

  // Production vs Development color schemes
  const linkHoverColor = isProduction ? "blue.100" : defaultLinkHoverColor;
  const hoverBgColor = isProduction
    ? "rgba(59, 130, 246, 0.15)"
    : defaultHoverBgColor;
  const textColor = isProduction ? "blue.50" : "white";
  const activeTextColor = isProduction ? "white" : linkHoverColor;
  const activeBgColor = isProduction
    ? "rgba(59, 130, 246, 0.25)"
    : hoverBgColor;

  // State to manage which submenu is currently open (accordion behavior)
  const [openSubmenu, setOpenSubmenu] = useState(null);

  const navItemsToUse = isProduction ? PROD_NAV_ITEMS : NAV_ITEMS;

  // Function to handle submenu toggle
  const handleSubmenuToggle = (navItemLabel) => {
    setOpenSubmenu((prevOpen) =>
      prevOpen === navItemLabel ? null : navItemLabel,
    );
  };

  // Auto-expand submenu if any child is active (only on route changes, not user interactions)
  useEffect(() => {
    if (!isCollapsed) {
      const activeSubmenu = navItemsToUse.find(
        (navItem) =>
          navItem.children && isNavItemActive(navItem, location.pathname),
      );
      if (activeSubmenu) {
        setOpenSubmenu(activeSubmenu.label);
      }
    }
  }, [location.pathname, isCollapsed, navItemsToUse]);

  return (
    <Stack spacing={1} p={isCollapsed ? 2 : 4} pb={isCollapsed ? 2 : 8}>
      {navItemsToUse?.map((navItem) => {
        const isActive = isNavItemActive(navItem, location.pathname);

        return (
          <Box key={navItem.label}>
            {navItem.children ? (
              <SidebarNavItemWithChildren
                navItem={navItem}
                isCollapsed={isCollapsed}
                isOpen={openSubmenu === navItem.label}
                onToggle={() => handleSubmenuToggle(navItem.label)}
                onClose={() => setOpenSubmenu(null)}
              />
            ) : (
              <Tooltip
                label={isCollapsed ? navItem.label : ""}
                placement="right"
                isDisabled={!isCollapsed}
                hasArrow
                bg={isProduction ? "blue.700" : "gray.800"}
                color="white"
              >
                <Link to={navItem.href}>
                  <Box
                    p={isCollapsed ? 2 : 3}
                    borderRadius="md"
                    fontSize="16px"
                    fontWeight={isActive ? 600 : 500}
                    color={isActive ? activeTextColor : textColor}
                    bg={isActive ? activeBgColor : "transparent"}
                    cursor="pointer"
                    _hover={{
                      bg: hoverBgColor,
                      color: linkHoverColor,
                    }}
                    transition="all 0.2s"
                    display="flex"
                    alignItems="center"
                    gap={isCollapsed ? 0 : 3}
                    justifyContent={isCollapsed ? "center" : "flex-start"}
                    minH="44px"
                  >
                    <Icon as={getNavIcon(navItem.label)} boxSize={5} />
                    {!isCollapsed && navItem.label}
                  </Box>
                </Link>
              </Tooltip>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

const SidebarNavItemWithChildren = ({
  navItem,
  isCollapsed = false,
  isOpen,
  onToggle,
  onClose,
}) => {
  const location = useLocation();

  // Always call hooks unconditionally
  const defaultLinkHoverColor = useColorModeValue("gray.900", "white");
  const defaultHoverBgColor = useColorModeValue("gray.50", "gray.600");

  // Check if we're in production environment
  const isProduction =
    process.env.REACT_APP_MONGODB_URL?.includes("https://api.");

  // Production vs Development color schemes
  const linkHoverColor = isProduction ? "blue.100" : defaultLinkHoverColor;
  const hoverBgColor = isProduction
    ? "rgba(59, 130, 246, 0.15)"
    : defaultHoverBgColor;
  const textColor = isProduction ? "blue.50" : "white";
  const activeTextColor = isProduction ? "white" : linkHoverColor;
  const activeBgColor = isProduction
    ? "rgba(59, 130, 246, 0.25)"
    : hoverBgColor;
  const menuBgColor = isProduction ? "blue.800" : "gray.800";
  const menuBorderColor = isProduction ? "blue.600" : "gray.600";

  const isParentActive = isNavItemActive(navItem, location.pathname);

  // If sidebar is collapsed, show as a simple menu with tooltip
  if (isCollapsed) {
    return (
      <Menu placement="right-start">
        <Tooltip
          label={navItem.label}
          placement="right"
          hasArrow
          bg={isProduction ? "blue.700" : "gray.800"}
          color="white"
          openDelay={300}
        >
          <MenuButton
            p={2}
            borderRadius="md"
            fontSize="16px"
            fontWeight={isParentActive ? 600 : 500}
            color={isParentActive ? activeTextColor : textColor}
            bg={isParentActive ? activeBgColor : "transparent"}
            cursor="pointer"
            _hover={{
              bg: hoverBgColor,
              color: linkHoverColor,
            }}
            _active={{
              bg: hoverBgColor,
              color: linkHoverColor,
            }}
            transition="all 0.2s"
            display="flex"
            alignItems="center"
            justifyContent="center"
            minH="44px"
            width="100%"
            border="none"
            outline="none"
            _focus={{
              boxShadow: "none",
              bg: hoverBgColor,
              color: linkHoverColor,
            }}
          >
            <Icon as={getNavIcon(navItem.label)} boxSize={5} />
          </MenuButton>
        </Tooltip>
        <MenuList
          bg={menuBgColor}
          borderColor={menuBorderColor}
          border="1px solid"
          boxShadow="lg"
          py={2}
          minW="200px"
          zIndex={1500}
          maxH="400px"
          overflowY="auto"
          css={{
            "&::-webkit-scrollbar": {
              width: "3px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: isProduction ? "#3182ce" : "#4a5568",
              borderRadius: "2px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: isProduction ? "#2c5aa0" : "#2d3748",
            },
            // Firefox scrollbar styling
            scrollbarWidth: "thin",
            scrollbarColor: isProduction
              ? "#3182ce transparent"
              : "#4a5568 transparent",
          }}
        >
          <Box px={3} py={2} borderBottom="1px" borderColor={menuBorderColor}>
            <Text fontSize="sm" fontWeight="600" color={textColor}>
              {navItem.label}
            </Text>
          </Box>
          {navItem.children.map((child) => {
            const isChildActive = isChildItemActive(
              child.href,
              location.pathname,
            );

            return (
              <Link key={child.label} to={child.href}>
                <MenuItem
                  bg={isChildActive ? activeBgColor : "transparent"}
                  color={isChildActive ? activeTextColor : textColor}
                  _hover={{
                    bg: hoverBgColor,
                    color: linkHoverColor,
                  }}
                  _focus={{
                    bg: hoverBgColor,
                    color: linkHoverColor,
                  }}
                  fontSize="14px"
                  fontWeight={isChildActive ? 600 : 400}
                  py={2}
                  px={4}
                >
                  {child.label}
                </MenuItem>
              </Link>
            );
          })}
        </MenuList>
      </Menu>
    );
  }

  return (
    <Box>
      <Box
        p={3}
        borderRadius="md"
        fontSize="16px"
        fontWeight={isParentActive ? 600 : 500}
        color={isParentActive ? activeTextColor : textColor}
        bg={isParentActive ? activeBgColor : "transparent"}
        cursor="pointer"
        onClick={onToggle}
        _hover={{
          bg: hoverBgColor,
          color: linkHoverColor,
        }}
        transition="all 0.2s"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Box display="flex" alignItems="center" gap={3}>
          <Icon as={getNavIcon(navItem.label)} boxSize={5} />
          <Text>{navItem.label}</Text>
        </Box>
        <Icon
          as={isOpen ? ChevronDownIcon : ChevronRightIcon}
          transition="all 0.2s"
          boxSize={4}
        />
      </Box>
      <Collapse in={isOpen}>
        <Stack pl={4} pt={2} spacing={1}>
          {navItem.children.map((child) => {
            const isChildActive = isChildItemActive(
              child.href,
              location.pathname,
            );

            return (
              <Link key={child.label} to={child.href} onClick={onClose}>
                <Box
                  p={2}
                  borderRadius="md"
                  fontSize="14px"
                  fontWeight={isChildActive ? 600 : 400}
                  color={isChildActive ? activeTextColor : textColor}
                  bg={isChildActive ? activeBgColor : "transparent"}
                  cursor="pointer"
                  _hover={{
                    bg: hoverBgColor,
                    color: linkHoverColor,
                  }}
                  transition="all 0.2s"
                  pl={3}
                >
                  {child.label}
                </Box>
              </Link>
            );
          })}
        </Stack>
      </Collapse>
    </Box>
  );
};

const MobileNav = () => {
  // Check if we're in production environment
  const isProduction =
    process.env.REACT_APP_MONGODB_URL?.includes("https://api.");
  const navItemsToUse = isProduction ? PROD_NAV_ITEMS : NAV_ITEMS;
  const bgColor = useColorModeValue("white", "white");
  const location = useLocation();

  // State to manage which submenu is currently open (accordion behavior)
  const [openMobileSubmenu, setOpenMobileSubmenu] = useState(null);

  // Function to handle submenu toggle
  const handleMobileSubmenuToggle = (navItemLabel) => {
    setOpenMobileSubmenu((prevOpen) =>
      prevOpen === navItemLabel ? null : navItemLabel,
    );
  };

  // Auto-expand submenu if any child is active (only on route changes, not user interactions)
  useEffect(() => {
    const activeSubmenu = navItemsToUse.find(
      (navItem) =>
        navItem.children && isNavItemActive(navItem, location.pathname),
    );
    if (activeSubmenu) {
      setOpenMobileSubmenu(activeSubmenu.label);
    }
  }, [location.pathname, navItemsToUse]);

  return (
    <Stack bg={bgColor} p={4} display={{ md: "none" }}>
      {navItemsToUse?.map((navItem) => (
        <MobileNavItem
          key={navItem?.label}
          {...navItem}
          isOpen={openMobileSubmenu === navItem.label}
          onToggle={() => handleMobileSubmenuToggle(navItem.label)}
        />
      ))}
    </Stack>
  );
};

const MobileNavItem = ({ label, children, href, isOpen, onToggle }) => {
  const location = useLocation();
  const iconColor = useColorModeValue("gray.600", "gray.200");
  const textColor = useColorModeValue("gray.600", "gray.200");
  const borderColor = useColorModeValue("white", "white");
  const childTextColor = useColorModeValue("gray.600", "gray.200");
  const hoverBgColor = useColorModeValue("gray.50", "gray.700");
  const activeTextColor = useColorModeValue("gray.900", "white");

  const isParentActive = isNavItemActive(
    { label, children, href },
    location.pathname,
  );

  return (
    <Stack spacing={4}>
      <Box
        py={2}
        px={3}
        as="a"
        bg={isParentActive ? hoverBgColor : "transparent"}
        borderRadius="md"
        justifyContent="space-between"
        alignItems="center"
        _hover={{
          textDecoration: "none",
          bg: hoverBgColor,
        }}
        onClick={children ? onToggle : undefined}
      >
        {!children ? (
          <Link to={href}>
            <Box display="flex" alignItems="center" gap={3}>
              <Icon
                as={getNavIcon(label)}
                boxSize={5}
                color={isParentActive ? activeTextColor : iconColor}
              />
              <Text
                fontWeight={isParentActive ? 700 : 600}
                color={isParentActive ? activeTextColor : textColor}
              >
                {label}
              </Text>
            </Box>
          </Link>
        ) : (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Box display="flex" alignItems="center" gap={3}>
              <Icon
                as={getNavIcon(label)}
                boxSize={5}
                color={isParentActive ? activeTextColor : iconColor}
              />
              <Text
                fontWeight={isParentActive ? 700 : 600}
                color={isParentActive ? activeTextColor : textColor}
              >
                {label}
              </Text>
            </Box>
            <Icon
              as={isOpen ? ChevronDownIcon : ChevronRightIcon}
              transition={"all .25s ease-in-out"}
              w={5}
              h={5}
              color={isParentActive ? activeTextColor : iconColor}
            />
          </Box>
        )}
      </Box>

      <Collapse in={isOpen} animateOpacity style={{ marginTop: "0!important" }}>
        <Stack
          mt={2}
          pl={4}
          borderLeft={1}
          borderStyle={"solid"}
          borderColor={borderColor}
          align={"start"}
        >
          {children &&
            children.map((child) => {
              const isChildActive = isChildItemActive(
                child.href,
                location.pathname,
              );

              return (
                <Box
                  as="a"
                  key={child.label}
                  py={2}
                  px={3}
                  borderRadius="md"
                  bg={isChildActive ? hoverBgColor : "transparent"}
                  _hover={{ bg: hoverBgColor }}
                  transition="all 0.2s"
                  onClick={onToggle}
                >
                  <Link to={child.href}>
                    <Text
                      color={isChildActive ? activeTextColor : childTextColor}
                      fontWeight={isChildActive ? 600 : 400}
                    >
                      {child.label}
                    </Text>
                  </Link>
                </Box>
              );
            })}
        </Stack>
      </Collapse>
    </Stack>
  );
};
const NAV_ITEMS = [
  {
    label: "Explore",
    href: "/explore",
  },
  {
    label: "Masterlists",
    children: [
      {
        label: "Film",
        href: "/film",
      },
      {
        label: "Personality",
        href: "/personality",
      },
      {
        label: "Theme",
        href: "/theme",
      },
      // {
      //     label: 'Location',
      //     href: '/location',
      // },
      {
        label: "Location",
        href: "/location",
      },
      {
        label: "Institution",
        href: "/institution",
      },
      // {
      //     label: 'Featured',
      //     href: '/featured',
      // },
      {
        label: "Event",
        href: "/event",
      },
    ],
  },
  {
    label: "Masters",
    children: [
      // {
      //     label: 'Countries',
      //     href: '/countries',
      // },
      // {
      //     label: 'Genres',
      //     href: '/genres',
      // },
      {
        label: "Research Categories",
        href: "/research-categories",
      },
      {
        label: "Subscribed user",
        href: "/subscribed-users",
      },
      // Admin Users is intentionally hidden from nav — access via /admin-users directly
      {
        label: "Static Words",
        href: "/static-words",
      },
      {
        label: "Document Types",
        href: "/document-types",
      },
    ],
  },
  {
    label: "Posts",
    href: "/posts",
  },
  // {
  //     label: 'Insights',
  //     href: '/insights',
  // },

  {
    label: "Calendar",
    children: [
      {
        label: "Events",
        href: "/calendar/events",
      },
      {
        label: "Focus of the day",
        href: "/calendar/focus-of-the-day",
      },
    ],
  },
  // {
  //     label: 'Projects',
  //     href: '/projects',
  // },
  // {
  //     label: 'Events',
  //     href: '/events',
  // },
  {
    label: "Objects",
    children: [
      {
        label: "Library",
        href: "/libraries",
      },
      {
        label: "Archive",
        href: "/archive",
      },
      {
        label: "Videos",
        href: "/videos",
      },
      {
        label: "File List",
        href: "/file-list",
      },
      {
        label: "Derive File",
        href: "/derive-file",
      },
      {
        label: "Load Data",
        href: "/load-data",
      },
      {
        label: "Service Dashboard",
        href: "/service-dashboard",
      },
    ],
  },
  {
    label: "Agraphy",
    children: [
      {
        label: "Load File",
        href: "agraphy/load-file",
      },

      // {
      //     label: 'Load Data',
      //     href: 'agraphy/load-data',
      // }
    ],
  },
  {
    label: "More",
    children: [
      {
        label: "Flip book",
        href: "/flip-books",
      },
      {
        label: "Virtual tour",
        href: "/virtual-tours",
      },
      {
        label: "Export",
        href: "/export",
      },
    ],
  },
  {
    label: "Public",
    href: "/public",
  },
  {
    label: "Audit log",
    href: "/audit-log",
  },
];

const PROD_NAV_ITEMS = [
  {
    label: "Explore",
    href: "/explore",
  },
  {
    label: "Masterlists",
    children: [
      {
        label: "Film",
        href: "/film",
      },
      {
        label: "Personality",
        href: "/personality",
      },
      {
        label: "Theme",
        href: "/theme",
      },
      {
        label: "Location",
        href: "/location",
      },
      {
        label: "Institution",
        href: "/institution",
      },
      {
        label: "Event",
        href: "/event",
      },
    ],
  },
  {
    label: "Masters",
    children: [
      {
        label: "Research Categories",
        href: "/research-categories",
      },
      {
        label: "Subscribed user",
        href: "/subscribed-users",
      },
      // Admin Users is intentionally hidden from nav — access via /admin-users directly
      {
        label: "Static Words",
        href: "/static-words",
      },
      {
        label: "Document Types",
        href: "/document-types",
      },
    ],
  },
  {
    label: "Posts",
    href: "/posts",
  },
  {
    label: "Calendar",
    children: [
      {
        label: "Events",
        href: "/calendar/events",
      },
      {
        label: "Focus of the day",
        href: "/calendar/focus-of-the-day",
      },
    ],
  },
  // {
  //     label: 'Projects',
  //     href: '/projects',
  // },
  {
    label: "Objects",
    children: [
      {
        label: "Library",
        href: "/libraries",
      },
      {
        label: "Archive",
        href: "/archive",
      },
      {
        label: "Videos",
        href: "/videos",
      },
      {
        label: "Load Data",
        href: "/load-data",
      },
      {
        label: "File List",
        href: "/file-list",
      },
      {
        label: "Service Dashboard",
        href: "/service-dashboard",
      },
    ],
  },
  {
    label: "Agraphy",
    children: [
      {
        label: "Load File",
        href: "agraphy/load-file",
      },
      // {
      //     label: 'Load Data',
      //     href: 'agraphy/load-data',
      // }
    ],
  },
  {
    label: "More",
    children: [
      {
        label: "Flip book",
        href: "/flip-books",
      },
      {
        label: "Virtual tour",
        href: "/virtual-tours",
      },
      {
        label: "Export",
        href: "/export",
      },
    ],
  },
  {
    label: "Public",
    href: "/public",
  },
  {
    label: "Audit log",
    href: "/audit-log",
  },
];
